import { Component } from '../helpers/helper';
import { state, User } from '../state';
import { webRTC } from '../webRTC';
import { Icon } from './icon';
import { transport } from '../firebaseTransport';
import { service } from '../service';

declare global {
	interface Window {
		adsbygoogle: any[];
	}
}

const SidebarMenuItem = Component((p: { user: User; icon?: string }) => {
	const showBanDialog = Vue.ref(false);

	function handleBan() {
		// Tell the banned user (best-effort, for the alert UX) — they will
		// also be force-disconnected by the server below.
		transport
			.broadcast({
				type: 'user_banned',
				user_id: p.user.id,
				channel_id: state.channel_id,
				banned_user_name: p.user.name
			})
			.catch(err => console.error('user_banned broadcast failed', err));

		// Persist the ban (IP + uid) and force-remove the user from RTDB.
		service.banUser(state.channel_id, p.user.id).catch(err => {
			console.error('ban_user failed', err);
			alert('Failed to ban user. Please try again.');
		});
		showBanDialog.value = false;
	}

	function confirmBan(e: Event) {
		e.stopPropagation();
		showBanDialog.value = true;
	}

	function cancelBan(e: Event) {
		e.stopPropagation();
		showBanDialog.value = false;
	}

	return () => (
		<div class="sidebar_menu_item">
			<div class={{ 'user-item-name': true, 'video-enabled': p.user.showStream }}>
				<span
					id={`circle-${p.user.me ? 'me' : p.user.id}`}
					class="user_indicator"
					style={{ backgroundColor: p.user.color }}
				></span>
				<span class="sidebar_menu_item_label">{p.user.name}</span>
				{p.user.handRaised && (
					<span class="hand-raise-badge" title="Hand raised">✋</span>
				)}
				{state.isHost && p.user.attentionSignal && (
					<span
						class={`attention-dot ${p.user.attentionSignal}`}
						title={p.user.attentionSignal === 'lost' ? "I'm lost" : 'Need help'}
						onClick={() => transport.clearAttentionForUser(p.user.id).catch(console.error)}
					></span>
				)}
				{p.user.connectionQuality && (
					<span class={`quality-indicator ${p.user.connectionQuality}`} title={`Connection: ${p.user.connectionQuality}`}>●</span>
				)}
				{state.isHost && !p.user.me && (
					<span
						class="host-control-btn"
						title="Mute user"
						onClick={() => transport.broadcast({ type: 'host_mute_user', target_id: p.user.id }).catch(console.error)}
					>
						<Icon name="microphone-slash" size="12px" />
					</span>
				)}
				{state.isHost && !p.user.me && (
					<span
						class="host-control-btn"
						title="Stop video"
						onClick={() => transport.broadcast({ type: 'host_stop_user_video', target_id: p.user.id }).catch(console.error)}
					>
						<Icon name="video-slash" size="12px" />
					</span>
				)}
				{state.isHost && !p.user.me && (
					<span class="ban_button" onClick={confirmBan} title="Kick/ban user">
						<Icon name="ban" size="12px" />
					</span>
				)}
				{!p.user.muted && (
					<span class="mute_notification">
						<Icon name="microphone" size="12px" />
					</span>
				)}
				{p.user.muted && (
					<span class="mute_notification">
						<Icon name="microphone-slash" size="12px" />
					</span>
				)}
			</div>
			{showBanDialog.value && (
				<div class="ban_dialog_overlay" onClick={cancelBan}>
					<div class="ban_dialog" onClick={(e) => e.stopPropagation()}>
						<h3>Kick/Ban User</h3>
						<p>Kick and ban '{p.user.name}' from the '{state.channel_id}' channel?</p>
						<div class="ban_dialog_buttons">
							<button onClick={cancelBan}>Cancel</button>
							<button class="danger" onClick={handleBan}>Kick & Ban</button>
						</div>
					</div>
				</div>
			)}
			{p.user.reactions.length > 0 && (
				<div class="sidebar-reactions">
					{p.user.reactions.map(r => (
						<span key={r.id} class="floating-reaction">{r.emoji}</span>
					))}
				</div>
			)}
			{p.user.showStream && (
				<video
					class={{ flip: p.user.me && state.sharingCam }}
					srcObject={p.user.stream}
					autoplay
					onClick={e => state.setMainVideo(p.user.stream)}
				></video>
			)}
		</div>
	);
});

const Thread = Component((p: { t: IThread }) => {
	return () => (
		<div class={{ chat_thread: true, 'is-gatheract': p.t.from === '[channel]' }}>
			<h4 class={{ chat_from: true, 'is-gatheract': p.t.from === '[channel]' }}>
				{p.t.from === '[channel]' ? 'GatherAct' : p.t.from}
			</h4>
			<div class="chat_bubbles">
				{p.t.messages.map((m, j) => (
					<div key={j} class="chat_bubble">
						{m.url && (
							<a target="_blank" href={m.url}>
								{m.text}
							</a>
						)}
						{!m.url && <span>{m.text}</span>}
					</div>
				))}
			</div>
		</div>
	);
});

const ChatInput = Component(() => {
	function onInput(e) {
		state.newChatMessage = e.target.value;
		if (state.newChatMessage && state.newChatMessage.includes('\n')) {
			const raw = state.newChatMessage.replace(/\n+$/, '');
			state.newChatMessage = null;
			const text = raw.trim();
			if (!text) return;
			const from_name = state.user?.name || 'Guest';
			transport.postMessage({ text, from_name })
				.catch(err => console.error('chat send failed', err));
		}
	}

	return () => (
		<div class="chat_input">
			<textarea rows="3" value={state.newChatMessage} onInput={onInput} placeholder="Enter message" />
		</div>
	);
});

const BannerAd = Component(() => {
	Vue.onMounted(() => {
		try {
			(window.adsbygoogle = window.adsbygoogle || []).push({});
		} catch (e) {
			console.error('AdSense error:', e);
		}
	});

	return () => (
		<div class="banner_ad">
			<script async src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-5764129772900917"
				crossorigin="anonymous"></script>
			<ins class="adsbygoogle"
				style="display:inline-block;width:239px;height:60px"
				data-ad-client="ca-pub-5764129772900917"
				data-ad-slot="7039753936"></ins>
		</div>
	);
});

const SidebarChat = Component(() => {
	const elmessages = Vue.ref(null);

	Vue.watch([state.messages], () => {
		if (elmessages) {
			elmessages.value.scrollTop = elmessages.value.scrollHeight;
		}
	});

	return () => (
		<div class="sidebar_chat">
			<div class="sidebar_section_header">
				<Icon name="chat-alt-2" size="14px" />
				<h3>Chat</h3>
			</div>
			<div class="chat_messages" ref={elmessages}>
				{state.messageThreads.map((t, i) => (
					<Thread key={i} t={t} />
				))}
			</div>
			<ChatInput />
			{/*<BannerAd />*/}
		</div>
	);
});

export const Sidebar = Component(() => {
	const participantsCollapsed = Vue.ref(
		localStorage.getItem('participants_collapsed') === 'true'
	);

	function toggleParticipants() {
		participantsCollapsed.value = !participantsCollapsed.value;
		localStorage.setItem('participants_collapsed', `${participantsCollapsed.value}`);
	}

	return () => {
		const raisedCount = state.users.filter(u => u.handRaised).length;
		const attentionCount = state.isHost ? state.users.filter(u => u.attentionSignal).length : 0;

		return (
		<div id="main_sidebar">
			<div class="sidebar_section_header">
				<Icon name="users" size="14px" />
				<h3>Participants</h3>
				<span class="sidebar_section_count">{state.users.length}</span>
				{raisedCount > 0 && <span class="raised-hand-count">✋ {raisedCount}</span>}
				{attentionCount > 0 && <span class="sidebar-attention-count">! {attentionCount}</span>}
				<button
					type="button"
					class={{ sidebar_section_toggle: true, collapsed: participantsCollapsed.value }}
					onClick={toggleParticipants}
					aria-label={participantsCollapsed.value ? 'Expand participants' : 'Collapse participants'}
					title={participantsCollapsed.value ? 'Expand participants' : 'Collapse participants'}
				>
					<Icon name="chevron-down" size="12px" />
				</button>
			</div>
			{!participantsCollapsed.value && (
				<div class={{ sidebar_users: true, app_loaded: state.appLoaded }}>
					{state.appLoaded && state.mainVideoStream && (
						<img id="app-image" src={state.appImageUrl} onClick={e => state.setMainVideo(null)} />
					)}
					{state.users.map(u => (
						<SidebarMenuItem key={u.id} user={u}></SidebarMenuItem>
					))}
				</div>
			)}
			<div class="sidebar_menu_divider"></div>
			<SidebarChat />
		</div>
		);
	};
});
